import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { getSettings } from '../../queries.js';
import { zql } from '../../schema.js';
import { computeCycleDayFromTherapyDate, daysBetweenTimestamps, isoDateFromTimestamp, resolveCycleStartDate, } from '../../util/protocol-cycles.js';
import { addDaysUtc, assertIsAuthenticated, EventLogs, getDosingWeight, getTherapySequenceInitialStatus, getTotalAmountOfSubstanceDecimal, } from '../../utils.js';
import { inputSequenceRequiresAdditionalApproval } from '../patient.mutators.js';
import { addEventLog, EventLogResources } from '../util.js';
import { createTherapySequence } from '../utils/therapy-sequence.js';
import { loadTherapyParameterContext, logAmmsMfnMessage, logTherapyParameterMedicines, nextPrescriptionInternalId, } from './add-therapy-parameter.js';
import { getDoseBasis } from './dose-basis.js';
import { assertTotalAmountsNotZero } from './medicine-total-amount.js';
// CY-280: the Duplicate prescriptions dialog copies several prescriptions (and guideline notes) to new dates in
// one transaction, all or nothing. Each copy is a new prescription at the first status with its own number; its
// doses are recalculated with the patient's values of today. The input schema is in patient.mutators.ts with the
// other prescription schemas.
/** The dialog moves the first copied day; the other days keep their gaps (parent section 3). */
export function shiftDatesKeepingGaps(sourceDates, newStart) {
    if (sourceDates.length === 0)
        return [];
    const first = Math.min(...sourceDates);
    const shift = daysBetweenTimestamps(first, newStart);
    return sourceDates.map(date => addDaysUtc(date, shift));
}
function lastCycleOf(protocol, existing) {
    const known = Object.keys(protocol.cycleStartDates ?? {}).map(Number);
    const used = existing.map(seq => seq.cycle ?? 0);
    return Math.max(protocol.cycles ?? protocol.protocol?.totalCycles ?? 1, ...known, ...used, 1);
}
/**
 * One new cycle after the protocol's last one, starting on the first copied date and holding no template
 * prescriptions. Refused when that date is not after the last prescription of the last cycle, so a cycle is
 * never inserted in the middle of the existing ones.
 */
export function planAddedCycle(protocol, existing, firstNewDate) {
    const daysRepetition = protocol.protocol?.daysRepetition ?? 0;
    const lastCycle = lastCycleOf(protocol, existing);
    const lastDates = existing.filter(seq => seq.cycle === lastCycle).map(seq => seq.therapyDate);
    const lastDate = lastDates.length ? Math.max(...lastDates) : resolveCycleStartDate(protocol, lastCycle, daysRepetition);
    if (firstNewDate <= lastDate) {
        throw new Error(`A new cycle of ${protocol.protocol?.name ?? 'the protocol'} must start after its last prescription (${isoDateFromTimestamp(lastDate)})`);
    }
    // the earlier cycles keep their recorded start; a cycle recorded nowhere yet gets the day-repetition one,
    // as Edit protocol writes it
    const cycleStartDates = {};
    for (let cycle = 1; cycle <= lastCycle; cycle++) {
        cycleStartDates[cycle] = isoDateFromTimestamp(resolveCycleStartDate(protocol, cycle, daysRepetition));
    }
    const cycle = lastCycle + 1;
    cycleStartDates[cycle] = isoDateFromTimestamp(firstNewDate);
    return {
        patientProtocolId: protocol.id,
        protocolName: protocol.protocol?.name ?? '',
        cycle,
        startDate: firstNewDate,
        previous: { cycles: protocol.cycles, cycleStartDates: protocol.cycleStartDates },
        cycles: cycle,
        cycleStartDates,
    };
}
function formatDose(value) {
    return value === null ? '-' : String(value);
}
/**
 * Plans the copies and the added cycles without touching the database: the checks the dialog already made are
 * repeated here, the copies are ordered by date and time so their numbers follow that order, and each copy's
 * cycle and day come from its own protocol.
 */
export function planDuplicates(params) {
    const { input, sources, existingByProtocol, characteristics, gfrLab, roundingModes, dayZeroProtocol, today } = params;
    const newGroupId = params.newGroupId ?? uuidv7;
    const sourceById = new Map(sources.map(source => [source.id, source]));
    const pairs = input.sources.map(item => {
        const source = sourceById.get(item.therapySequenceId);
        if (!source || source.deletedAt != null || source.patientId !== input.patientId) {
            throw new Error('A prescription to copy was not found on this patient');
        }
        if (item.newDate < today)
            throw new Error('Prescription date cannot be in the past');
        return { item, source };
    });
    const protocols = new Map();
    for (const { source } of pairs) {
        if (!source.patientProtocolId)
            continue;
        if (!source.patientProtocol)
            throw new Error('Patient Protocol not found');
        if (source.patientProtocol.endDate != null) {
            throw new Error(`${source.patientProtocol.protocol?.name ?? 'The protocol'} is ended. Reactivate the protocol first.`);
        }
        protocols.set(source.patientProtocolId, source.patientProtocol);
    }
    const cycles = [];
    for (const patientProtocolId of new Set(input.addCycleFor)) {
        const protocol = protocols.get(patientProtocolId);
        if (!protocol)
            throw new Error('A cycle can only be added to a protocol of the copied prescriptions');
        const firstNewDate = Math.min(...pairs.filter(({ source }) => source.patientProtocolId === patientProtocolId).map(({ item }) => item.newDate));
        cycles.push(planAddedCycle(protocol, existingByProtocol.get(patientProtocolId) ?? [], firstNewDate));
    }
    const addedCycleByProtocol = new Map(cycles.map(cycle => [cycle.patientProtocolId, cycle]));
    const groupIds = new Map();
    const copies = pairs.map(({ item, source }) => {
        let cycle = null;
        let day = null;
        if (source.patientProtocol) {
            const added = addedCycleByProtocol.get(source.patientProtocol.id);
            const anchor = added
                ? { startDate: source.patientProtocol.startDate, cycleStartDates: added.cycleStartDates }
                : source.patientProtocol;
            const position = computeCycleDayFromTherapyDate(anchor, item.newDate, source.patientProtocol.protocol?.daysRepetition ?? 0, dayZeroProtocol);
            cycle = position.cycle;
            day = position.day;
        }
        let sequenceGroupId = null;
        if (source.isNote && source.sequenceGroupId) {
            sequenceGroupId = groupIds.get(source.sequenceGroupId) ?? newGroupId();
            groupIds.set(source.sequenceGroupId, sequenceGroupId);
        }
        const overrides = new Map(item.medicines.map(medicine => [medicine.medicineId, medicine.regularDose]));
        const sourceMedicines = source.medicine.filter(medicine => medicine.deletedAt == null);
        for (const medicineId of overrides.keys()) {
            if (!sourceMedicines.some(medicine => medicine.id === medicineId)) {
                throw new Error('A dose was given for a medicine that is not on the copied prescription');
            }
        }
        const doseChanges = [];
        const medicines = sourceMedicines.map((medicine) => {
            const regularDose = overrides.get(medicine.id) ?? medicine.regularDose;
            if (regularDose !== medicine.regularDose) {
                doseChanges.push(`${medicine.activeSubstance?.name ?? medicine.activeSubstanceId ?? ''}: ${formatDose(medicine.regularDose)} → ${formatDose(regularDose)}`);
            }
            const totalAmount = getTotalAmountOfSubstanceDecimal({
                calculationMethod: (medicine.calculationMethod ?? 'ABSOLUTE'),
                dose: regularDose ?? 0,
                doseProportion: medicine.doseProportion ?? 100,
                roundingMode: medicine.activeSubstanceId ? roundingModes.get(medicine.activeSubstanceId) : undefined,
                dosePrecision: medicine.precisionDecimals,
                height: characteristics?.height ?? null,
                weight: getDosingWeight(characteristics),
                gfr: gfrLab?.gfr ?? null,
                bsa: characteristics?.bsa ?? null,
            });
            return {
                activeSubstanceId: medicine.activeSubstanceId,
                prescribedNameId: medicine.prescribedNameId,
                autIdem: medicine.autIdem,
                formType: medicine.formType,
                calculationMethod: medicine.calculationMethod,
                regularDose,
                recommendedDose: medicine.recommendedDose,
                precisionDecimals: medicine.precisionDecimals,
                doseProportion: medicine.doseProportion,
                notes: medicine.notes,
                totalAmount,
            };
        });
        return { source, therapyDate: item.newDate, cycle, day, sequenceGroupId, medicines, doseChanges };
    });
    // numbers follow the order of the new dates, then the times, then the order the rows were selected in
    const order = new Map(copies.map((copy, index) => [copy, index]));
    copies.sort((a, b) => a.therapyDate - b.therapyDate ||
        a.source.time.localeCompare(b.source.time) ||
        (order.get(a) ?? 0) - (order.get(b) ?? 0));
    return { copies, cycles };
}
/**
 * Prescription numbers restart every day and are read back from the rows already saved, so within one
 * transaction the next number is also kept in memory: two copies on the same day never share a number.
 */
export async function allocateInternalIds(tx, therapyDates) {
    const nextByPrefix = new Map();
    const ids = [];
    /* the numbers depend on each other, so they are taken one after the other on purpose */
    /* oxlint-disable no-await-in-loop */
    for (const therapyDate of therapyDates) {
        const { internalId, prescriptionNumber } = await nextPrescriptionInternalId(tx, therapyDate);
        const prefix = internalId.slice(0, internalId.length - String(prescriptionNumber).length);
        const number = Math.max(prescriptionNumber, nextByPrefix.get(prefix) ?? 0);
        nextByPrefix.set(prefix, number + 1);
        ids.push(`${prefix}${number}`);
    }
    /* oxlint-enable no-await-in-loop */
    return ids;
}
function loadSources(tx, input) {
    const ids = input.sources.map(source => source.therapySequenceId);
    return tx.run(zql.patientTherapySequence
        .where('id', 'IN', ids)
        .where('patientId', '=', input.patientId)
        .where('deletedAt', 'IS', null)
        .related('medicine', medicine => medicine.where('deletedAt', 'IS', null).related('activeSubstance'))
        .related('patientProtocol', patientProtocol => patientProtocol.related('protocol')));
}
export async function duplicateTherapySequences(tx, input, ctx) {
    assertIsAuthenticated(ctx);
    const userId = ctx.user.id;
    const sources = await loadSources(tx, input);
    if (sources.length !== new Set(input.sources.map(source => source.therapySequenceId)).size) {
        throw new Error('A prescription to copy was not found on this patient');
    }
    const protocolIds = [...new Set(sources.map(source => source.patientProtocolId).filter(isString))];
    const existing = protocolIds.length
        ? await tx.run(zql.patientTherapySequence.where('patientProtocolId', 'IN', protocolIds).where('deletedAt', 'IS', null))
        : [];
    const existingByProtocol = new Map();
    for (const seq of existing) {
        if (!seq.patientProtocolId)
            continue;
        const list = existingByProtocol.get(seq.patientProtocolId) ?? [];
        list.push({ cycle: seq.cycle, therapyDate: seq.therapyDate });
        existingByProtocol.set(seq.patientProtocolId, list);
    }
    const context = await loadTherapyParameterContext(tx, input.patientId, sources.flatMap(source => source.medicine.map(medicine => medicine.activeSubstanceId)));
    const settings = await getSettings(tx);
    const { copies, cycles } = planDuplicates({
        input,
        sources: sources,
        existingByProtocol,
        characteristics: context.characteristics,
        gfrLab: context.gfrLab,
        roundingModes: context.roundingModes,
        dayZeroProtocol: settings?.dayZeroProtocol,
        today: new Date().setUTCHours(0, 0, 0, 0),
    });
    const prescriptions = copies.filter(copy => !copy.source.isNote);
    const internalIds = await allocateInternalIds(tx, prescriptions.map(copy => copy.therapyDate));
    const internalIdByCopy = new Map(prescriptions.map((copy, index) => [copy, internalIds[index]]));
    /* copies are saved one after the other so History and AMMS keep the numbering order */
    /* oxlint-disable no-await-in-loop */
    for (const copy of copies) {
        await saveCopy(tx, copy, { internalId: internalIdByCopy.get(copy) ?? null, context, userId, ctx });
    }
    /* oxlint-enable no-await-in-loop */
    // the added cycles do not depend on each other
    const now = Date.now();
    await Promise.all(cycles.map(async (cycle) => {
        await tx.mutate.patientProtocols.update({
            id: cycle.patientProtocolId,
            cycles: cycle.cycles,
            cycleStartDates: cycle.cycleStartDates,
            updatedAt: now,
            updatedBy: userId,
        });
        if (tx.location !== 'server')
            return;
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientProtocol,
            resourceId: cycle.patientProtocolId,
            action: 'update',
            domain: 'patient',
            event: EventLogs.PATIENT_PROTOCOL_CYCLES_UPDATED,
            patches: createPatch(cycle.previous, { cycles: cycle.cycles, cycleStartDates: cycle.cycleStartDates }),
            status: 'OK',
            performedBy: userId,
            performedAt: now,
            details: {
                patientId: input.patientId,
                patientName: context.patient?.fullName ?? '',
                protocolName: cycle.protocolName,
                addedCycles: `${cycle.cycle}: ${isoDateFromTimestamp(cycle.startDate)}`,
                reason: 'Duplicate prescriptions',
            },
        });
    }));
}
/**
 * What a copy takes from its source (parent section 4): the medication, carrier, volume, form, duration, time,
 * doctor, protocol and notes. Status, validations, production, administration, observations, consumables and
 * the chair are not here, so a copy can never carry them.
 */
export function copiedSequenceFields(copy) {
    const { source } = copy;
    const common = {
        patientId: source.patientId,
        protocolId: source.protocolId,
        patientProtocolId: source.patientProtocolId,
        therapyDate: copy.therapyDate,
        cycle: copy.cycle,
        day: copy.day,
        time: source.time,
        note: source.note,
        responsibleDoctorId: source.responsibleDoctorId,
    };
    if (source.isNote)
        return common;
    return {
        ...common,
        therapyNote: source.therapyNote,
        pharmacyNote: source.pharmacyNote,
        carrierSolutionId: source.carrierSolutionId,
        applicationFormId: source.applicationFormId,
        duration: source.duration,
        volume: source.volume,
        volumeMode: source.volumeMode,
    };
}
function isString(value) {
    return typeof value === 'string';
}
async function saveCopy(tx, copy, params) {
    const { internalId, context, userId, ctx } = params;
    const { source } = copy;
    const isProtocol = Boolean(source.patientProtocolId);
    const copiedFrom = source.internalId ?? source.id;
    const copied = copiedSequenceFields(copy);
    if (source.isNote) {
        // a guideline note has no number, no medicines and no AMMS message; a copied group stays one group
        await createTherapySequence(tx, {
            data: {
                ...copied,
                sequenceGroupId: copy.sequenceGroupId,
                status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
                isNote: true,
            },
            log: {
                event: isProtocol ? EventLogs.PATIENT_INSTRUCTION_NOTE_PROTOCOL_ADDED : EventLogs.PATIENT_INSTRUCTION_NOTE_ADDED,
                details: {
                    patientId: source.patientId,
                    patientName: context.patient?.fullName ?? '',
                    copiedFrom,
                },
            },
        }, ctx);
        return;
    }
    const medicineRows = copy.medicines.map(medicine => ({
        patientId: source.patientId,
        therapyDate: copy.therapyDate,
        activeSubstanceId: medicine.activeSubstanceId,
        prescribedNameId: medicine.prescribedNameId,
        autIdem: medicine.autIdem,
        formType: medicine.formType,
        calculationMethod: medicine.calculationMethod,
        regularDose: medicine.regularDose,
        recommendedDose: medicine.recommendedDose,
        precisionDecimals: medicine.precisionDecimals,
        doseProportion: medicine.doseProportion,
        notes: medicine.notes,
        totalAmount: medicine.totalAmount,
    }));
    // a dose that rounds to 0 with today's values is no dose (CY-317)
    assertTotalAmountsNotZero(tx, medicineRows.map(row => row.totalAmount));
    const requiresAdditionalApproval = await inputSequenceRequiresAdditionalApproval(tx, copy.medicines
        .filter(medicine => medicine.activeSubstanceId)
        .map(medicine => ({
        activeSubstanceId: medicine.activeSubstanceId,
        prescribedNameId: medicine.prescribedNameId ?? undefined,
        autIdem: medicine.autIdem,
        calculationMethod: (medicine.calculationMethod ?? 'ABSOLUTE'),
        regularDose: medicine.regularDose ?? 0,
        precisionDecimals: medicine.precisionDecimals ?? 0,
        doseProportion: medicine.doseProportion ?? undefined,
    })));
    const { sequence, medicines, now } = await createTherapySequence(tx, {
        data: {
            ...copied,
            internalId,
            status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
            isNote: false,
            ...getDoseBasis(context.characteristics, context.gfrLab),
        },
        medicines: medicineRows,
        log: {
            event: isProtocol ? EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_ADDED : EventLogs.PATIENT_THERAPY_SEQUENCE_ADDED,
            details: {
                id: internalId,
                patientName: context.patient?.fullName ?? '',
                patientId: source.patientId,
                therapyDate: String(copy.therapyDate),
                time: source.time,
                protocolName: source.patientProtocol?.protocol?.name,
                cycle: copy.cycle === null ? undefined : String(copy.cycle),
                day: copy.day === null ? undefined : String(copy.day),
                copiedFrom,
                doseChanges: copy.doseChanges.join(', ') || undefined,
            },
        },
    }, ctx);
    if (tx.location === 'server') {
        await Promise.all([
            ...logTherapyParameterMedicines(tx, {
                medicines,
                context,
                internalId: internalId ?? '',
                event: isProtocol
                    ? EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_ADDED
                    : EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_ADDED,
                now,
                userId,
            }),
            logAmmsMfnMessage(tx, {
                sequence: {
                    id: sequence.id,
                    internalId: internalId ?? '',
                    applicationFormId: source.applicationFormId,
                    carrierSolutionId: source.carrierSolutionId,
                    volume: source.volume,
                },
                medicines,
                change: 'add',
                now,
            }),
        ]);
    }
}
//# sourceMappingURL=duplicate-therapy-sequences.js.map